import first from "lodash/first";
import isEqual from "lodash/isEqual";
import noop from "lodash/noop";
import size from "lodash/size";
import values from "lodash/values";
import PropTypes from "prop-types";
import React, { Component } from "react";

import { history } from "../../utils/chat/chatNavigation";
import MinimizedViewAlertingExperts from "./MinimizedViewAlertingExperts";
import MinimizedViewAskQuestion from "./MinimizedViewAskQuestion";
import MinimizedViewNotifications from "./MinimizedViewNotifications";
import MinimizedViewOpenQuestions from "./MinimizedViewOpenQuestions";
import { postMessageToParent } from "../../utils/Utils";
import { PostMessage } from "../../constants/chat";

const DEFAULT_MAXIMIZE_PATH = "ask";

class MinimizedView extends Component {
    constructor() {
        super();

        this.processPostMessage = this.processPostMessage.bind(this);
    }

    componentDidMount() {
        postMessageToParent(this.shouldHideWidgetWraper ? PostMessage.HIDE : PostMessage.SHOW);
        window.addEventListener("message", this.processPostMessage);
    }

    componentDidUpdate() {
        if (!this.props.isLoading) {
            postMessageToParent(this.shouldHideWidgetWraper ? PostMessage.HIDE : PostMessage.SHOW);
        }
    }

    componentWillUnmount() {
        window.removeEventListener("message", this.processPostMessage);
    }

    get shouldDisplayNotificationsView() {
        return this.props.isLoggedIn && this.props.unreadMessagesCount > 0;
    }

    get shouldDisplayAlertingExpertsView() {
        return this.props.isLoggedIn && size(this.props.unansweredOpenQuestionsId) > 0;
    }

    get shouldDisplayOpenQuestionsView() {
        return (
            this.props.isLoggedIn &&
            size(this.props.allOpenQuestionsId) > 0 &&
            !isEqual(this.props.allOpenQuestionsId.sort(), this.props.unansweredOpenQuestionsId.sort())
        );
    }

    get shouldHideWidgetWraper() {
        const isAskQuestionView =
            !this.shouldDisplayNotificationsView &&
            !this.shouldDisplayOpenQuestionsView &&
            !this.shouldDisplayAlertingExpertsView;

        return isAskQuestionView && !this.props.displayAskQuestion;
    }

    get pathToMaximize() {
        const { allOpenQuestionsId, notificationsGroupedByQuestion, unansweredOpenQuestionsId } = this.props;

        if (this.shouldDisplayNotificationsView) {
            const { questionUuid, responseId } = first(first(values(notificationsGroupedByQuestion)));

            return size(notificationsGroupedByQuestion) === 1
                ? `question/${questionUuid}?responseId=${responseId}`
                : "list";
        }

        if (this.shouldDisplayOpenQuestionsView) {
            return size(allOpenQuestionsId) === 1 ? `question/${first(allOpenQuestionsId)}` : "list";
        }

        if (this.shouldDisplayAlertingExpertsView) {
            return size(unansweredOpenQuestionsId) === 1 ? `question/${first(unansweredOpenQuestionsId)}` : "list";
        }

        return "ask";
    }

    processPostMessage({ data: { action, params } }) {
        switch (action) {
            case PostMessage.MAXIMIZE: {
                history.push(this.pathToMaximize || DEFAULT_MAXIMIZE_PATH);
                break;
            }
            case PostMessage.MAXIMIZE_ASK_QUESTION: {
                if (this.props.disableAskNewQuestionFromUi) {
                    history.push(this.pathToMaximize || DEFAULT_MAXIMIZE_PATH);
                } else {
                    history.push("ask");
                }
                break;
            }
            case PostMessage.NAVIGATE: {
                const path = first(params);

                if (this.props.disableAskNewQuestionFromUi && path === "ask") {
                    history.push(this.pathToMaximize || DEFAULT_MAXIMIZE_PATH);
                } else {
                    history.push(path);
                }
                break;
            }
            default: {
                noop();
            }
        }
    }

    render() {
        const {
            allOpenQuestionsId,
            displayAskQuestion,
            isLoading,
            labels,
            notificationsGroupedByAvatar,
            unreadMessagesCount,
        } = this.props;

        if (isLoading) {
            return null;
        }

        if (this.shouldDisplayNotificationsView) {
            return (
                <MinimizedViewNotifications
                    notificationsGroupedByAvatar={notificationsGroupedByAvatar}
                    pathToMaximize={this.pathToMaximize}
                    unreadMessagesCount={unreadMessagesCount}
                />
            );
        }

        if (this.shouldDisplayOpenQuestionsView) {
            return (
                <MinimizedViewOpenQuestions
                    allOpenQuestionsId={allOpenQuestionsId}
                    pathToMaximize={this.pathToMaximize}
                />
            );
        }

        if (this.shouldDisplayAlertingExpertsView) {
            return <MinimizedViewAlertingExperts pathToMaximize={this.pathToMaximize} />;
        }

        return displayAskQuestion ? (
            <MinimizedViewAskQuestion label={labels.askBubble} pathToMaximize={this.pathToMaximize} />
        ) : null;
    }
}

MinimizedView.propTypes = {
    allOpenQuestionsId: PropTypes.arrayOf(PropTypes.string).isRequired,
    disableAskNewQuestionFromUi: PropTypes.bool.isRequired,
    displayAskQuestion: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isLoggedIn: PropTypes.bool.isRequired,
    labels: PropTypes.shape({
        askBubble: PropTypes.string.isRequired,
        askButton: PropTypes.string.isRequired,
        askFormHeader: PropTypes.string.isRequired,
    }).isRequired,
    notificationsGroupedByAvatar: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                avatarSrc: PropTypes.string.isRequired,
                questionUuid: PropTypes.string.isRequired,
                responseId: PropTypes.number.isRequired,
                unreadMessages: PropTypes.number.isRequired,
            })
        ).isRequired
    ).isRequired,
    notificationsGroupedByQuestion: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                avatarSrc: PropTypes.string.isRequired,
                questionUuid: PropTypes.string.isRequired,
                responseId: PropTypes.number.isRequired,
                unreadMessages: PropTypes.number.isRequired,
            })
        ).isRequired
    ).isRequired,
    unansweredOpenQuestionsId: PropTypes.arrayOf(PropTypes.string).isRequired,
    unreadMessagesCount: PropTypes.number.isRequired,
};

export default MinimizedView;
